import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { useGetClientAdminsClient } from '~/hooks';
import { BILLING_ADDRESS, ERROR_MESSAGE, SUCCESS_MESSAGE } from '../constants';
import type { ContractInfoPresentation } from '../presentations';
import { useGetStripeCreditCard } from './useGetStripeCreditCard';
import { usePostClientAdminsCardRegistrations } from './usePostClientAdminsCardRegistrations';

type PresentationProps = React.ComponentProps<typeof ContractInfoPresentation>;

type IUseContractInfo = () => {
  state: {
    /** 導入企業情報 */
    data: ReturnType<typeof useGetClientAdminsClient>['data'];
    /** Stripe 既定カード情報。null = 未登録（404） */
    creditCardData: ReturnType<typeof useGetStripeCreditCard>['data'];
    /** カード変更フォームを展開しているか */
    isFormExpanded: PresentationProps['isFormExpanded'];
    /** SetupIntent 発行 / カード登録に失敗したときのエラー文言 */
    errorMessage: PresentationProps['errorMessage'];
    /** カード登録成功時の案内文言 */
    successMessage: PresentationProps['successMessage'];
    /** 保存ボタンを非活性にするか */
    isSubmitDisabled: PresentationProps['isSubmitDisabled'];
  };
  actions: {
    handleToggleForm: PresentationProps['onToggleForm'];
    handleSubmitCard: PresentationProps['onSubmitCard'];
  };
};

/**
 * ご契約情報のコンポーネントフック
 *
 * - threeLetterCode を Cookie から取得し、各 API hook に渡す
 * - 導入企業情報（会社情報 / プラン情報 / ご担当者情報 / ご請求先情報の表示元）を取得する
 * - お支払い方法セクションのカード情報を取得し、カード変更フォームの開閉と登録を扱う
 *   - 編集ボタン押下でカード変更フォームを展開し、SetupIntent（client_secret）を発行する
 *   - 保存ボタン押下で Payment Element の入力内容を Stripe に送信し、カードを登録する
 */
export const useContractInfo: IUseContractInfo = () => {
  const threeLetterCode = getThreeLetterCode() ?? '';

  const { data } = useGetClientAdminsClient({ threeLetterCode });
  const { data: creditCardData, mutate } = useGetStripeCreditCard({ threeLetterCode });

  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<PresentationProps['errorMessage']>(undefined);
  const [successMessage, setSuccessMessage] =
    useState<PresentationProps['successMessage']>(undefined);
  /** confirmSetup 実行中か（二重送信を防ぐため保存ボタンを非活性にする） */
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * 発行済み SetupIntent の client_secret。
   *
   * フォームを格納してもクリアせず保持し、再展開時の再発行を避ける。
   * SetupIntent は決済失敗後も `requires_payment_method` に戻って再利用できるため、
   * 開閉のたびに発行すると使われないまま放置される孤児 SetupIntent が Stripe 上に溜まる。
   *
   * confirmSetup に渡すだけでなく保存ボタンの非活性判定にも使う（= レンダリング中に読む）ため、
   * ref ではなく state で保持する。
   */
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);

  const { trigger, isMutating } = usePostClientAdminsCardRegistrations(threeLetterCode, {
    onSuccess: responseData => {
      setClientSecret(responseData.card_registration.client_secret);
    },
    onError: error => {
      // 発行に失敗した client_secret は保持しない（次回の展開で再取得させる）
      setClientSecret(undefined);
      setErrorMessage(
        error.response?.status === 404 ? ERROR_MESSAGE.notCardPayment : ERROR_MESSAGE.unexpected
      );
    },
  });

  /**
   * カード変更フォームの展開 / 格納を切り替える
   *
   * 展開時は client_secret が未取得の場合のみ SetupIntent を発行する。
   * 格納時はエラー文言をクリアし、client_secret は再利用のため保持する。
   */
  const handleToggleForm: PresentationProps['onToggleForm'] = () => {
    if (isFormExpanded) {
      setIsFormExpanded(false);
      setErrorMessage(undefined);
      return;
    }

    setIsFormExpanded(true);
    setSuccessMessage(undefined);
    if (!clientSecret) {
      trigger();
    }
  };

  /**
   * Payment Element の入力内容を Stripe に送信し、カードを登録する
   *
   * deferred モード（Elements に clientSecret を渡さない）では、confirmSetup の前に
   * elements.submit() で入力値のバリデーション・収集を行う必要がある。
   *
   * 成功時はフォームを閉じ、反映ラグの案内を表示する（新カードの既定化は
   * setup_intent.succeeded Webhook が非同期で行うため、画面のカード情報は即時更新されない）。
   */
  const handleSubmitCard: PresentationProps['onSubmitCard'] = async (stripe, elements) => {
    // 非活性化しているため通常は到達しないが、型を絞るために早期リターンする
    if (!clientSecret) return;

    setIsSubmitting(true);
    setErrorMessage(undefined);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message ?? ERROR_MESSAGE.cardRegistrationFailed);
      setIsSubmitting(false);
      return;
    }

    const { error: confirmError } = await stripe.confirmSetup({
      elements,
      clientSecret,
      confirmParams: {
        payment_method_data: {
          // fields で 'never' にしたフィールドは自前で渡す義務がある（詳細は BILLING_ADDRESS の JSDoc）
          billing_details: { address: BILLING_ADDRESS },
        },
      },
      // カード決済のみのためリダイレクトは発生しないが、'always' だと return_url が必須になる
      redirect: 'if_required',
    });
    setIsSubmitting(false);

    if (confirmError) {
      setErrorMessage(confirmError.message ?? ERROR_MESSAGE.cardRegistrationFailed);
      return;
    }

    setIsFormExpanded(false);
    setSuccessMessage(SUCCESS_MESSAGE);
    // Webhook（setup_intent.succeeded）の反映ラグにより旧カード / 404 が返る可能性があるが、
    // 再取得は 1 回のみでよい（ポーリングはしない）。ラグの案内は SUCCESS_MESSAGE 側で行う。
    mutate();
  };

  return {
    state: {
      data,
      creditCardData,
      isFormExpanded,
      errorMessage,
      successMessage,
      // client_secret 未取得（発行中含む）では confirmSetup を実行できない
      isSubmitDisabled: !clientSecret || isMutating || isSubmitting,
    },
    actions: { handleToggleForm, handleSubmitCard },
  };
};
